import { useMemo } from 'react';

import { Checkbox } from 'components/ui/checkbox';
import ReportHealthReading from 'components/reports/ReportHealthReading';
import { ScrollArea } from 'components/ui/scroll-area';
import { getTitleComment } from 'lib/utils';
import { ReportQuestionnaire, CategorizedReadingData } from '../../types';
import useAuthStore from 'store/auth';

interface InputParametersProps {
  scanData: CategorizedReadingData;
  questionnaireData: ReportQuestionnaire[];
}

export interface DropdownOption {
  label: string;
  isSelected: boolean;
  choice: string;
  selectedChoices: string | string[];
}

const DropdownQuestion = ({
  question,
  locale,
}: {
  question: ReportQuestionnaire;
  locale: string;
}) => {
  if (question?.eng_options.includes('Initiate Scan')) return;

  const langQuestion =
    locale === 'es' ? question?.spanish_question : question?.eng_question;
  const { title, comment } = getTitleComment(langQuestion || '');

  const langOptions =
    locale === 'es' ? question?.spanish_options : question?.eng_options;
  const options = useMemo(
    () =>
      JSON.parse(langOptions || '[]')
        ?.map((option: string) => {
          const { title: choice, comment } = getTitleComment(option);

          let nonStringOption;
          try {
            nonStringOption =
              question?.eng_choices &&
              Array.isArray(eval(question.eng_choices as string))
                ? eval(question.eng_choices as string)?.find(
                    (opt: unknown) => typeof opt !== 'string'
                  )
                : undefined;
          } catch (error) {
            console.error(error);
          }

          return {
            label: `${choice} ${
              comment
                ? comment.startsWith('(')
                  ? comment
                  : `- ${comment}`
                : ''
            }`,
            isSelected: question.eng_choices.includes(choice),
            choice,
            selectedChoices:
              choice === 'Other' && nonStringOption
                ? Object.values(nonStringOption)?.[0]
                : undefined,
          };
        })
        .filter((option: { choice: string }) => option?.choice),
    [question.eng_choices, question?.eng_choices]
  );

  return (
    <li className='font-medium text-primary'>
      <div>
        <h2 className='font-semibold leading-tight'>{title}</h2>
        {comment ? <p className='leading-tight'>({comment})</p> : null}
      </div>

      <div className='grid grid-cols-1 gap-2 mt-3 lg:grid-cols-2 gap-x-8'>
        {options.map(
          (
            { label, isSelected, choice, selectedChoices }: DropdownOption,
            ind: number
          ) => (
            <div key={`${label}-${ind}`}>
              <div className='flex items-center gap-x-2'>
                <Checkbox
                  className='data-[state=checked]:bg-primary data-[state=checked]:text-white border-primary pointer-events-none'
                  checked={isSelected}
                />
                <p className='text-sm leading-tight'>{label}</p>
              </div>
              {isSelected && ['Other', 'Otor'].includes(choice) ? (
                <div className='mt-2 ml-6 border-b-2 border-primary'>
                  <p className='text-sm leading-tight'>{selectedChoices}</p>
                </div>
              ) : null}
            </div>
          )
        )}
      </div>
    </li>
  );
};

const InputQuestion = ({
  question,
  locale,
}: {
  question: ReportQuestionnaire;
  locale: string;
}) => {
  const langQuestion =
    locale === 'es' ? question?.spanish_question : question?.eng_question;
  const { title, comment } = getTitleComment(langQuestion || '');

  const answer =
    locale === 'es' ? question.spanish_choices : question.eng_choices;

  return (
    <li className='font-medium text-primary'>
      <div>
        <div className='font-semibold leading-tight'>{title}</div>

        {comment ? <p className='leading-tight'>({comment})</p> : null}
      </div>

      <div className='grid mt-3 gap-x-2 grid-cols-[10px_1fr]'>
        <div className='w-1.5 h-full rounded-2xl bg-primary block' />

        <p className='leading-tight cursor-pointer'>{answer || 'N/A'}</p>
      </div>
    </li>
  );
};

export default function InputParameters({
  scanData,
  questionnaireData,
}: InputParametersProps) {
  const locale = useAuthStore((store) => store.locale);

  return (
    <div className='grid grid-cols-1 lg:py-4 lg:grid-cols-12'>
      <div className='order-2 col-span-1 lg:order-1 lg:border-r-2 lg:border-dashed lg:col-span-5 lg:border-primary/80'>
        <ScrollArea className='h-fit lg:h-[calc(100vh-15.25rem)] xl:h-[calc(100vh-13.5rem)] lg:pr-6'>
          <ReportHealthReading data={scanData} />
        </ScrollArea>
      </div>
      <div className='order-1 col-span-1 pl-2 lg:col-span-7 lg:order-1'>
        <ScrollArea className='h-fit lg:h-[calc(100vh-15.25rem)] xl:h-[calc(100vh-13.5rem)]'>
          <ul className='px-4 py-4 space-y-4 list-decimal lg:px-10'>
            {questionnaireData?.map((question, index) => {
              if (
                ['dropdown', 'single-select', 'multi-select'].includes(
                  question.question_type
                )
              )
                return (
                  <DropdownQuestion
                    key={`${question.q_id}-${index}`}
                    question={question}
                    locale={locale}
                  />
                );
              return (
                <InputQuestion
                  key={`${question.q_id}-${index}`}
                  question={question}
                  locale={locale}
                />
              );
            })}
          </ul>
        </ScrollArea>
      </div>
    </div>
  );
}
